import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  MenuItem,
  Grid,
  IconButton,
  Divider,
} from '@mui/material';
import DateInputWithArrows from '../components/DateInputWithArrows';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { DataGrid } from '@mui/x-data-grid';
import {
  getProjects,
  getProject,
  createProject,
  updateProject,
  deleteProject,
  getCompanies,
} from '../services/api';
import ProjectDetailsModal from '../components/ProjectDetailsModal';
import { formatCurrency, formatDateEuropean } from '../utils/dateUtils';
import useResponsive from '../components/responsive/useResponsive';
import ResponsiveDataList from '../components/responsive/ResponsiveDataList';
import ResponsivePageSection from '../components/responsive/ResponsivePageSection';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

const PROJECT_DETAIL_TABS = {
  otherDocuments: 7,
};

function Projects() {
  const { t } = useTranslation();
  const { isMobile } = useResponsive();
  const [searchParams, setSearchParams] = useSearchParams();
  const [projects, setProjects] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [editingProject, setEditingProject] = useState(null);
  const [sortModel, setSortModel] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [detailsInitialTab, setDetailsInitialTab] = useState(0);
  const [companies, setCompanies] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    investor: '',
    location: '',
    supervisor: '', // Keep for backwards compatibility
    startDate: '',
    endDate: '',
    status: 'NOT_STARTED',
    mainContractor: '',
    workType: '',
    projectPhase: 1,
    agreedPrice: '',
    notes: '',
    supervisors: [],
  });

  const columns = [
    { field: 'name', headerName: t('projects.fields.name'), width: 200, sortable: true },
    { field: 'investor', headerName: t('projects.fields.investor'), width: 150, sortable: true },
    { field: 'location', headerName: t('projects.fields.location'), width: 150, sortable: true },
    { 
      field: 'supervisors', 
      headerName: t('projects.fields.supervisors'),
      width: 200, 
      sortable: false,
      valueFormatter: (params) => {
        if (!params.value || params.value.length === 0) {
          return t('projects.noSupervisors');
        }
        return params.value.map(s => s.supervisorName).join(', ');
      }
    },
    { 
      field: 'startDate', 
      headerName: t('projects.fields.startDate'),
      width: 130,
      sortable: true,
      valueFormatter: (params) => formatDateEuropean(params.value)
    },
    { 
      field: 'endDate', 
      headerName: t('projects.fields.endDate'),
      width: 130,
      sortable: true,
      valueFormatter: (params) => params.value ? formatDateEuropean(params.value) : '-'
    },
    { field: 'status', headerName: t('projects.fields.status'), width: 130, sortable: true },
    { field: 'mainContractor', headerName: t('projects.fields.mainContractor'), width: 150, sortable: true },
    { field: 'workType', headerName: t('projects.fields.workType'), width: 130, sortable: true },
    { field: 'projectPhase', headerName: t('projects.fields.phase'), width: 90, sortable: true },
    { 
      field: 'agreedPrice', 
      headerName: t('projects.fields.agreedPrice'),
      width: 130,
      sortable: true,
      valueFormatter: (params) => 
        formatCurrency(params.value)
    },
    {
      field: 'actions',
      headerName: t('common.actions'),
      width: 250,
      renderCell: (params) => (
        <Box>
          <Button onClick={() => handleEdit(params.row)}>{t('common.edit')}</Button>
          <Button onClick={() => handleViewDetails(params.row)}>{t('common.details')}</Button>
          <Button color="error" onClick={() => handleDelete(params.row.id)}>{t('common.delete')}</Button>
        </Box>
      ),
    },
  ];

  useEffect(() => {
    fetchProjects();
  }, [sortModel]);

  useEffect(() => {
    if (loading) return;
    const openId = Number(searchParams.get('open'));
    if (!openId) return;
    if (detailsModalOpen && selectedProject?.id === openId) return;
    const project = projects.find((item) => item.id === openId);
    if (project) {
      const tabKey = searchParams.get('tab');
      handleViewDetails(project, PROJECT_DETAIL_TABS[tabKey] ?? 0);
    }
  }, [loading, projects, searchParams, detailsModalOpen, selectedProject]);

  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const response = await getCompanies();
        setCompanies(response.data || []);
      } catch (error) {
        console.error('Error fetching companies:', error);
      }
    };
    fetchCompanies();
  }, []);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const sort = sortModel[0]?.field;
      const order = sortModel[0]?.sort;
      const response = await getProjects({ sort, order });
      setProjects(response.data);
    } catch (error) {
      console.error('Error fetching projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = () => {
    setOpen(true);
    setEditingProject(null);
    setFormData({
      name: '',
      investor: '',
      location: '',
      supervisor: '',
      startDate: '',
      endDate: '',
      status: 'NOT_STARTED',
      mainContractor: '',
      workType: '',
      projectPhase: 1,
      agreedPrice: '',
      notes: '',
      supervisors: [],
    });
  };

  const handleClose = () => {
    setOpen(false);
    setEditingProject(null);
  };

  const handleViewDetails = (project, tab = 0) => {
    setDetailsInitialTab(tab);
    setSelectedProject(project);
    setDetailsModalOpen(true);
  };

  const handleProjectUpdate = async () => {
    // Refresh the projects list to get updated data
    await fetchProjects();
    
    // Update the selected project with the latest data
    if (selectedProject) {
      try {
        const response = await getProject(selectedProject.id);
        setSelectedProject(response.data);
      } catch (error) {
        console.error('Error fetching updated project:', error);
      }
    }
  };

  const handleEdit = (project) => {
    setEditingProject(project);
    setFormData({
      name: project.name,
      investor: project.investor,
      location: project.location,
      supervisor: project.supervisor,
      startDate: project.startDate ? project.startDate.split('T')[0] : '',
      status: project.status,
      mainContractor: project.mainContractor,
      workType: project.workType,
      projectPhase: project.projectPhase,
      agreedPrice: project.agreedPrice,
      notes: project.notes || '', // Ensure notes is never null
      supervisors: project.supervisors || [],
    });
    setOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm(t('projects.deleteConfirm'))) {
      try {
        await deleteProject(id);
        fetchProjects();
      } catch (error) {
        console.error('Error deleting project:', error);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        ...formData,
        projectPhase: parseInt(formData.projectPhase),
        agreedPrice: parseFloat(formData.agreedPrice),
      };

      if (editingProject) {
        delete submitData.endDate;
        await updateProject(editingProject.id, submitData);
      } else {
        await createProject(submitData);
      }
      handleClose();
      fetchProjects();
    } catch (error) {
      console.error('Error saving project:', error);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const addSupervisor = () => {
    setFormData({
      ...formData,
      supervisors: [...formData.supervisors, { 
        supervisingField: '', 
        supervisorCompany: '', 
        supervisorName: '', 
        supervisorNotes: '' 
      }]
    });
  };

  const removeSupervisor = (index) => {
    setFormData({
      ...formData,
      supervisors: formData.supervisors.filter((_, i) => i !== index)
    });
  };

  const updateSupervisor = (index, field, value) => {
    const updatedSupervisors = [...formData.supervisors];
    updatedSupervisors[index] = { ...updatedSupervisors[index], [field]: value };
    setFormData({ ...formData, supervisors: updatedSupervisors });
  };

  return (
    <ResponsivePageSection>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1.5, mb: 2 }}>
        <Typography variant="h4">{t('projects.title')}</Typography>
        <Button variant="contained" onClick={handleOpen}>{t('projects.addProject')}</Button>
      </Box>

      {isMobile ? (
        <ResponsiveDataList
          rows={projects}
          getTitle={(project) => project.name}
          getSubtitle={(project) => `${project.location || '-'} - ${project.status}`}
          renderFields={(project) => (
            <Stack spacing={0.5}>
              <Typography variant="body2"><b>{t('projects.fields.investor')}:</b> {project.investor || '-'}</Typography>
              <Typography variant="body2"><b>{t('projects.fields.startDate')}:</b> {project.startDate ? formatDateEuropean(project.startDate) : '-'}</Typography>
              <Typography variant="body2"><b>{t('projects.fields.endDate')}:</b> {project.endDate ? formatDateEuropean(project.endDate) : '-'}</Typography>
            </Stack>
          )}
          renderActions={(project) => (
            <>
              <Button size="small" onClick={() => handleEdit(project)}>{t('common.edit')}</Button>
              <Button size="small" onClick={() => handleViewDetails(project)}>{t('common.details')}</Button>
              <Button size="small" color="error" onClick={() => handleDelete(project.id)}>{t('common.delete')}</Button>
            </>
          )}
        />
      ) : (
        <DataGrid
          rows={projects}
          columns={columns}
          pageSize={10}
          loading={loading}
          autoHeight
          sortingMode="server"
          onSortModelChange={(model) => setSortModel(model)}
          sortModel={sortModel}
          disableColumnMenu={false}
        />
      )}

      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth fullScreen={isMobile}>
        <DialogTitle>{editingProject ? t('projects.editProject') : t('projects.addProject')}</DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <TextField
              fullWidth
              margin="normal"
              name="name"
              label={t('projects.fields.projectName')}
              value={formData.name}
              onChange={handleChange}
              required
            />
            <TextField
              fullWidth
              margin="normal"
              name="investor"
              label={t('projects.fields.investor')}
              value={formData.investor}
              onChange={handleChange}
              required
            />
            <TextField
              fullWidth
              margin="normal"
              name="location"
              label={t('projects.fields.location')}
              value={formData.location}
              onChange={handleChange}
              required
            />
            {/* Supervisors Section */}
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="h6">{t('projects.fields.supervisors')}</Typography>
                <Button
                  startIcon={<AddIcon />}
                  onClick={addSupervisor}
                  variant="outlined"
                  size="small"
                >
                  {t('projects.addSupervisor')}
                </Button>
              </Box>
              {formData.supervisors.map((supervisor, index) => (
                <Box key={index} sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={3}>
                      <TextField
                        fullWidth
                        label={t('projects.fields.supervisingField')}
                        value={supervisor.supervisingField}
                        onChange={(e) => updateSupervisor(index, 'supervisingField', e.target.value)}
                        placeholder={t('projects.supervisingFieldPlaceholder')}
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <TextField
                        fullWidth
                        label={t('projects.fields.supervisorCompany')}
                        value={supervisor.supervisorCompany}
                        onChange={(e) => updateSupervisor(index, 'supervisorCompany', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <TextField
                        fullWidth
                        label={t('projects.fields.supervisorName')}
                        value={supervisor.supervisorName}
                        onChange={(e) => updateSupervisor(index, 'supervisorName', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={2}>
                      <TextField
                        fullWidth
                        label={t('projects.fields.notes')}
                        value={supervisor.supervisorNotes}
                        onChange={(e) => updateSupervisor(index, 'supervisorNotes', e.target.value)}
                        multiline
                        rows={1}
                      />
                    </Grid>
                    <Grid item xs={12} sm={1}>
                      <IconButton
                        onClick={() => removeSupervisor(index)}
                        color="error"
                        sx={{ mt: 1 }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Grid>
                  </Grid>
                </Box>
              ))}
            </Box>
            <DateInputWithArrows
              fullWidth
              margin="normal"
              name="startDate"
              label={t('projects.fields.startDate')}
              value={formData.startDate}
              onChange={handleChange}
              required
            />
            {!editingProject && (
              <DateInputWithArrows
                fullWidth
                margin="normal"
                name="endDate"
                label={t('projects.fields.endDate')}
                value={formData.endDate}
                onChange={handleChange}
              />
            )}
            <TextField
              fullWidth
              margin="normal"
              name="status"
              label={t('projects.fields.status')}
              select
              value={formData.status}
              onChange={handleChange}
              required
            >
              <MenuItem value="NOT_STARTED">{t('status.notStarted')}</MenuItem>
              <MenuItem value="IN_PROGRESS">{t('status.inProgress')}</MenuItem>
              <MenuItem value="DONE">{t('status.done')}</MenuItem>
            </TextField>
            <TextField
              fullWidth
              margin="normal"
              name="mainContractor"
              label={t('projects.fields.mainContractor')}
              select
              value={formData.mainContractor}
              onChange={handleChange}
              required
            >
              {(companies || []).map((company) => (
                <MenuItem key={company.id} value={company.name}>
                  {company.name}
                </MenuItem>
              ))}
              {formData.mainContractor &&
                !(companies || []).some((company) => company.name === formData.mainContractor) && (
                  <MenuItem value={formData.mainContractor}>
                    {formData.mainContractor} {t('projects.legacyValue')}
                  </MenuItem>
                )}
            </TextField>
            <TextField
              fullWidth
              margin="normal"
              name="workType"
              label={t('projects.fields.workType')}
              value={formData.workType}
              onChange={handleChange}
              required
            />
            <TextField
              fullWidth
              margin="normal"
              name="projectPhase"
              label={t('projects.fields.projectPhase')}
              type="number"
              value={formData.projectPhase}
              onChange={handleChange}
              required
              inputProps={{ min: 1, step: 1 }}
            />
            <TextField
              fullWidth
              margin="normal"
              name="agreedPrice"
              label={t('projects.fields.agreedPriceEur')}
              type="number"
              value={formData.agreedPrice}
              onChange={handleChange}
              required
              inputProps={{ min: 0, step: "0.01" }}
            />
            <TextField
              fullWidth
              margin="normal"
              name="notes"
              label={t('projects.fields.notes')}
              value={formData.notes}
              onChange={handleChange}
              multiline
              rows={3}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>{t('common.cancel')}</Button>
            <Button type="submit" variant="contained">{t('common.save')}</Button>
          </DialogActions>
        </form>
      </Dialog>

      <ProjectDetailsModal
        open={detailsModalOpen}
        onClose={() => {
          setDetailsModalOpen(false);
          setDetailsInitialTab(0);
          if (searchParams.get('open')) {
            setSearchParams({}, { replace: true });
          }
        }}
        project={selectedProject}
        onProjectUpdate={handleProjectUpdate}
        initialTab={detailsInitialTab}
      />
    </ResponsivePageSection>
  );
}

export default Projects; 